<template>
    <div class="container">
        <AllowPermission :permission="PERMISSION_CODES.CMS_CUSTOMER_IMPORT">
            <Card>
                <div class="customer-import-page">
                    <div class="page-header">
                        <div>
                            <div class="eyebrow">Cova customer import</div>
                            <h2>Import customers safely</h2>
                            <p>Analyze the Cova export, review skipped rows, then create only valid new customers.</p>
                        </div>
                        <router-link to="/customers">
                            <Button borderOnly noMargin type="button">View Customers</Button>
                        </router-link>
                    </div>

                    <section class="section-block">
                        <div class="section-heading">
                            <div>
                                <h3>Upload and analyze</h3>
                                <p>Supports Cova customer exports up to {{ formatBytes(maxFileSizeBytes) }}.</p>
                            </div>
                            <span class="section-status" :class="{ ready: !!importFile }">
                                {{ importFile ? "File ready" : "Waiting for file" }}
                            </span>
                        </div>

                        <label class="file-drop">
                            <span class="file-title">Cova customer export</span>
                            <span class="file-name">{{ selectedFileName || "Choose Cova customer export" }}</span>
                            <input
                                type="file"
                                accept=".csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                                :disabled="preflighting || importing"
                                @click="$event.target.value = null"
                                @change="handleFileSelect"
                            />
                        </label>

                        <div class="action-row">
                            <Button type="button" :disabled="!importFile || preflighting || importing" @click="runPreflight">
                                {{ preflighting ? "Analyzing..." : "Run Analysis" }}
                            </Button>
                            <Button v-if="preflight || importResult" borderOnly noMargin type="button" :disabled="preflighting || importing" @click="resetWorkflow">
                                Reset
                            </Button>
                        </div>
                    </section>

                    <section class="section-block">
                        <div class="section-heading">
                            <div>
                                <h3>Review report</h3>
                                <p>Rows with duplicate, invalid, or already known identities are skipped automatically.</p>
                            </div>
                            <span class="section-status" :class="{ ready: preflight && preflight.readyToImport }">
                                {{ preflight ? (preflight.readyToImport ? "Ready" : "Needs review") : "Not checked" }}
                            </span>
                        </div>

                        <div v-if="preflighting" class="empty-state">Reading Cova export...</div>
                        <div v-else-if="!preflight" class="empty-state">Run analysis to preview the import.</div>
                        <div v-else>
                            <div class="metric-grid">
                                <div v-for="metric in summaryMetrics" :key="metric.label" class="metric-cell">
                                    <span>{{ metric.label }}</span>
                                    <strong>{{ metric.value }}</strong>
                                </div>
                            </div>

                            <div v-if="preflight.missingHeaders && preflight.missingHeaders.length" class="issue-list danger">
                                <h4>Missing required headers</h4>
                                <p>{{ preflight.missingHeaders.join(", ") }}</p>
                            </div>

                            <div v-if="issueRows.length" class="issue-list warning">
                                <h4>Skipped row rules</h4>
                                <div v-for="issue in issueRows" :key="issue.type" class="issue-row">
                                    <span>{{ issue.message }}</span>
                                    <strong>{{ formatNumber(issue.count) }}</strong>
                                </div>
                            </div>

                            <div class="duplicate-grid" v-if="hasDuplicateSummary">
                                <div class="preview-card">
                                    <h4>Top duplicate emails</h4>
                                    <table v-if="topDuplicateEmails.length">
                                        <thead>
                                            <tr>
                                                <th>Email</th>
                                                <th>Rows</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="row in topDuplicateEmails" :key="row.value">
                                                <td>{{ row.value }}</td>
                                                <td>{{ formatNumber(row.rows) }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <p v-else>No duplicate emails detected.</p>
                                </div>
                                <div class="preview-card">
                                    <h4>Top duplicate phones</h4>
                                    <table v-if="topDuplicatePhones.length">
                                        <thead>
                                            <tr>
                                                <th>Phone</th>
                                                <th>Rows</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="row in topDuplicatePhones" :key="row.value">
                                                <td>{{ row.value }}</td>
                                                <td>{{ formatNumber(row.rows) }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <p v-else>No duplicate phones detected.</p>
                                </div>
                            </div>

                            <div v-for="sample in sampleGroups" :key="sample.key" class="preview-card">
                                <h4>{{ sample.label }}</h4>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Row</th>
                                            <th>Name</th>
                                            <th>Email</th>
                                            <th>Phone</th>
                                            <th>Reason</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="row in sample.rows" :key="sample.key + row.rowNum">
                                            <td>#{{ row.rowNum }}</td>
                                            <td>{{ [row.firstName, row.lastName].filter(Boolean).join(" ") || "-" }}</td>
                                            <td>{{ row.email || "-" }}</td>
                                            <td>{{ row.phone || "-" }}</td>
                                            <td>{{ row.reason || "-" }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </section>

                    <section class="section-block">
                        <div class="section-heading">
                            <div>
                                <h3>Import</h3>
                                <p>Create valid new customers only. Existing, duplicate, and invalid rows remain skipped.</p>
                            </div>
                            <span class="section-status" :class="{ ready: importResult }">
                                {{ importResult ? "Imported" : "Pending" }}
                            </span>
                        </div>

                        <div v-if="importResult" class="success-panel">
                            <strong>{{ formatNumber(importResult.importedCustomers) }} customers imported</strong>
                            <p>{{ formatNumber(importResult.addressesCreated) }} customer addresses created.</p>
                        </div>
                        <div class="action-row">
                            <Button type="button" :disabled="!canImport" @click="runImport">
                                {{ importing ? "Importing..." : "Import Valid New Customers" }}
                            </Button>
                        </div>
                    </section>
                </div>
            </Card>
        </AllowPermission>
    </div>
</template>

<script>
import { mapActions } from "vuex"
import Button from "../../components/UI/Button.vue"
import Card from "../../components/UI/Card.vue"
import { errMsg } from "../../utils/common"
import { http } from "../../utils/http"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import { PERMISSION_CODES } from "../../store/permission"

export default {
    components: { Button, Card, AllowPermission },
    data() {
        return {
            importFile: null,
            preflighting: false,
            importing: false,
            preflight: null,
            importResult: null,
            maxFileSizeBytes: 50 * 1024 * 1024,
            PERMISSION_CODES,
        }
    },
    computed: {
        selectedFileName() {
            return this.importFile ? this.importFile.name : ""
        },
        canImport() {
            return !!this.importFile && !!this.preflight && this.preflight.readyToImport && !this.importResult && !this.preflighting && !this.importing
        },
        summaryMetrics() {
            if (!this.preflight) return []
            return [
                { label: "Rows in file", value: this.formatNumber(this.preflight.totalRows) },
                { label: "Valid new customers", value: this.formatNumber(this.preflight.creatableRows) },
                { label: "Skipped rows", value: this.formatNumber(this.preflight.skippedRows) },
                { label: "No email or phone", value: this.formatNumber(this.preflight.rowsWithoutIdentity) },
                { label: "Invalid gram limit", value: this.formatNumber(this.preflight.invalidGramLimitRows) },
                { label: "Duplicate file rows", value: this.formatNumber(this.preflight.duplicateFileRows) },
                { label: "Existing customers", value: this.formatNumber(this.preflight.existingCustomerRows) },
            ]
        },
        issueRows() {
            return (this.preflight && this.preflight.issues ? this.preflight.issues : []).filter((issue) => issue.type !== "missing_headers")
        },
        topDuplicateEmails() {
            return this.preflight?.duplicateSummary?.topDuplicateEmails || []
        },
        topDuplicatePhones() {
            return this.preflight?.duplicateSummary?.topDuplicatePhones || []
        },
        hasDuplicateSummary() {
            return this.topDuplicateEmails.length || this.topDuplicatePhones.length
        },
        sampleGroups() {
            if (!this.preflight || !this.preflight.samples) return []
            const labels = {
                missingIdentity: "Rows missing email and phone",
                invalidEmail: "Invalid emails",
                invalidPhone: "Invalid phones",
                invalidGramLimit: "Invalid gram limits",
                duplicateInFile: "Duplicates inside this file",
                existingCustomer: "Rows already in High Ties",
            }
            return Object.keys(labels)
                .map((key) => ({
                    key,
                    label: labels[key],
                    rows: this.preflight.samples[key] || [],
                }))
                .filter((group) => group.rows.length)
        },
    },
    mounted() {
        this.setData({ pageTitle: "Import Customers" })
    },
    methods: {
        ...mapActions("misc", ["setData"]),
        formatNumber(value) {
            return Number(value || 0).toLocaleString()
        },
        formatBytes(bytes) {
            return `${Math.round(bytes / (1024 * 1024))}MB`
        },
        resetWorkflow() {
            this.preflight = null
            this.importResult = null
        },
        handleFileSelect(e) {
            const file = e.target.files[0]
            if (!file) return
            if (file.size > this.maxFileSizeBytes) {
                e.target.value = null
                this.importFile = null
                this.resetWorkflow()
                return this.$toast.error(`File size cannot be greater than ${this.formatBytes(this.maxFileSizeBytes)}.`)
            }
            this.importFile = file
            this.resetWorkflow()
        },
        buildFormData(confirm = false) {
            const formData = new FormData()
            formData.append("csvFile", this.importFile)
            if (confirm) formData.append("confirm", "true")
            return formData
        },
        async runPreflight() {
            if (!this.importFile || this.preflighting) return
            this.preflighting = true
            this.preflight = null
            this.importResult = null
            try {
                const res = await http.post("/customers/cova-import/preflight", this.buildFormData())
                this.preflight = res.data.result
                this.$toast.success("Cova customer analysis completed")
            } catch (err) {
                this.$toast.error(errMsg(err, "Cova customer analysis failed"))
            } finally {
                this.preflighting = false
            }
        },
        async runImport() {
            if (!this.canImport) return
            this.importing = true
            try {
                const res = await http.post("/customers/cova-import/run", this.buildFormData(true))
                this.importResult = res.data.result
                this.preflight = res.data.result
                this.$toast.success("Cova customers imported successfully")
            } catch (err) {
                this.$toast.error(errMsg(err, "Cova customer import failed"))
            } finally {
                this.importing = false
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

$borderColor: $lightBorderColor;
$mutedTextColor: rgba($textColor, 0.68);

.container {
    padding: $cardPaddingY $cardPaddingX;
}

.customer-import-page {
    display: flex;
    flex-direction: column;
    gap: $sectionGap;
}

.page-header,
.section-heading {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    align-items: flex-start;
}

.eyebrow {
    color: $primaryColor;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0;
    text-transform: uppercase;
}

.page-header h2,
.section-heading h3 {
    margin: 0;
    color: $textColor;
}

.page-header p,
.section-heading p,
.preview-card p,
.success-panel p {
    margin: 4px 0 0;
    color: $mutedTextColor;
}

.section-block {
    border: 1px solid $borderColor;
    border-radius: 8px;
    padding: 18px;
    background: $lightCardBg;
}

.section-status {
    border: 1px solid $borderColor;
    border-radius: 999px;
    color: $mutedTextColor;
    font-size: 0.78rem;
    font-weight: 700;
    padding: 6px 10px;
    white-space: nowrap;
}

.section-status.ready {
    border-color: rgba($successColor, 0.35);
    color: darken($successColor, 12%);
    background: rgba($successColor, 0.08);
}

.file-drop {
    display: flex;
    flex-direction: column;
    gap: 8px;
    border: 1px dashed $borderColor;
    border-radius: 8px;
    cursor: pointer;
    margin-top: 16px;
    padding: 18px;
}

.file-title {
    color: $mutedTextColor;
    font-size: 0.82rem;
    font-weight: 700;
}

.file-name {
    color: $textColor;
    font-weight: 700;
}

.file-drop input {
    display: none;
}

.action-row {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin-top: 16px;
}

.empty-state,
.success-panel {
    border: 1px solid $borderColor;
    border-radius: 8px;
    color: $mutedTextColor;
    margin-top: 16px;
    padding: 18px;
}

.success-panel {
    border-color: rgba($successColor, 0.35);
    color: $textColor;
    background: rgba($successColor, 0.08);
}

.metric-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 12px;
    margin-top: 16px;
}

.metric-cell {
    border: 1px solid $borderColor;
    border-radius: 8px;
    padding: 14px;
}

.metric-cell span {
    color: $mutedTextColor;
    display: block;
    font-size: 0.78rem;
    font-weight: 700;
}

.metric-cell strong {
    color: $textColor;
    display: block;
    font-size: 1.35rem;
    margin-top: 4px;
}

.issue-list,
.preview-card {
    border: 1px solid $borderColor;
    border-radius: 8px;
    margin-top: 16px;
    padding: 14px;
}

.issue-list h4,
.preview-card h4 {
    color: $textColor;
    font-size: 1rem;
    margin: 0 0 10px;
}

.issue-list.warning {
    border-color: rgba($warningColor, 0.45);
    background: rgba($warningColor, 0.08);
}

.issue-list.danger {
    border-color: rgba($dangerColor, 0.45);
    background: rgba($dangerColor, 0.08);
}

.issue-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 6px 0;
}

.duplicate-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 12px;
}

table {
    border-collapse: collapse;
    width: 100%;
}

th,
td {
    border-bottom: 1px solid $borderColor;
    color: $textColor;
    padding: 8px;
    text-align: left;
    vertical-align: top;
}

th {
    color: $mutedTextColor;
    font-size: 0.78rem;
    text-transform: uppercase;
}

@media (max-width: 768px) {
    .page-header,
    .section-heading {
        flex-direction: column;
    }
}
</style>
